import { FC, useState } from 'react';
import { createZodFormResolver, Form, lt, useAppClockService, useDialog } from '@mews/framework';
import { useCommanderSelector } from '@mews-commander/utils';
import { ProductPricing } from '@mews-commander/data-access';
import { getReservationsNonDefaultProductOrderItems } from '@mews-commander/legacy-entities/src/selectors/orderItems';
import { SchemaFormFields } from '@mews-commander/form';
import { TextKey } from '@mews-commander/enums';
import { getServiceProducts } from '@mews-commander/legacy-entities/src/selectors/products';
import { getService } from '@mews-commander/legacy-entities/src/ducks/services';
import { getFirstCheckedReservation } from '../../../selectors';
import { FormLayout } from '../../FormLayout';
import { getSplitFormInitialValues } from '../selectors';
import { SplitReservationDialog } from './SplitReservationDialog';
import { SplitReservationFormSchema, SplitReservationFormValues } from './types';
import { useSplitReservationMutation } from './useSplitReservationMutation';

interface SplitReservationFormProps {
    scope: string;
    reservationIds: string[];
    onSuccess: () => void;
}

export const SplitReservationForm: FC<SplitReservationFormProps> = ({ scope, reservationIds, onSuccess }) => {
    const [splitReservationDate, setSplitReservationDate] = useState<string | null>(null);
    const [{ active, onClose }, openDialog] = useDialog();
    const checkedReservation = useCommanderSelector((state) => getFirstCheckedReservation(state, { scope }));
    const appClockService = useAppClockService();
    const defaultValues = useCommanderSelector((state) => getSplitFormInitialValues(state, { appClockService }));
    const mutation = useSplitReservationMutation(reservationIds);
    const service = useCommanderSelector((state) => {
        if (checkedReservation == null) {
            return undefined;
        }
        return getService(state, { id: checkedReservation.serviceId });
    });
    const isProRatedService = service?.options.proRataCharging;
    const reservationProductOrderItems = useCommanderSelector((state) => {
        if (checkedReservation == null) {
            return [];
        }
        return getReservationsNonDefaultProductOrderItems(state, {
            serviceOrderIds: [checkedReservation.id],
            serviceId: checkedReservation.serviceId,
        });
    });

    const serviceProducts = useCommanderSelector((state) => {
        if (checkedReservation == null) {
            return [];
        }
        return getServiceProducts(state, {
            serviceId: checkedReservation.serviceId,
        });
    });

    const nonProRatedAbsoluteProducts = serviceProducts.filter(
        (product) =>
            reservationProductOrderItems.find((item) => item.data.value.productId === product.id) != null &&
            !product.options.proRataCharging &&
            product.pricing.discriminator === ProductPricing.Absolute
    );

    const splitReservation = (date: string) => {
        mutation.mutate({ date }, { onSuccess });
    };

    const onSubmit = ({ date }: SplitReservationFormValues) => {
        if (nonProRatedAbsoluteProducts.length > 0 && isProRatedService) {
            setSplitReservationDate(date);
            openDialog();
        } else {
            splitReservation(date);
        }
    };

    return (
        <>
            <Form
                onSubmit={onSubmit}
                resolver={createZodFormResolver(SplitReservationFormSchema)}
                values={defaultValues}
                dataTestForm="split-form"
            >
                <FormLayout loading={mutation.isPending} vertical={false}>
                    <SchemaFormFields
                        schema={SplitReservationFormSchema}
                        formDefinition={{
                            fields: {
                                date: {
                                    label: lt(TextKey.ReservationSplit),
                                    field: {
                                        type: 'date',
                                        mode: 'localized',
                                        dataTestId: 'split-reservation-input',
                                    },
                                },
                            },
                        }}
                    />
                </FormLayout>
            </Form>
            {splitReservationDate !== null && (
                <SplitReservationDialog
                    active={active}
                    nonProRatedAbsoluteProducts={nonProRatedAbsoluteProducts}
                    onClose={onClose}
                    onSplitReservation={() => splitReservation(splitReservationDate)}
                />
            )}
        </>
    );
};
